import React, { useState, useEffect } from 'react';
import { RefreshCw, Trash2, BarChart3, Image, Info } from 'lucide-react';
import { useImagePreloader } from '../hooks/useImagePreloader';

/**
 * Image Cache Manager Component
 * For debugging and monitoring image cache performance
 */
const ImageCacheManager = () => {
  const [stats, setStats] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const { getCacheStats, clearCache } = useImagePreloader();

  // Update stats periodically
  useEffect(() => {
    const updateStats = () => {
      setStats(getCacheStats());
    };

    updateStats();
    const interval = setInterval(updateStats, 2000); // Update every 2 seconds

    return () => clearInterval(interval);
  }, [getCacheStats]);

  const handleClearCache = () => {
    clearCache();
    setStats(getCacheStats());
  };

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 bg-blue-500 text-white p-2 rounded-full shadow-lg hover:bg-blue-600 transition-colors z-50"
        title="Show Image Cache Stats"
      >
        <BarChart3 size={20} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-sm z-50">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center">
          <Image size={16} className="mr-2" />
          Image Cache
        </h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          ×
        </button>
      </div>

      {stats && (
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-600">Cached Images:</span>
            <span className="font-medium">{stats.cacheSize}/{stats.maxCacheSize}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Loading:</span>
            <span className="font-medium">{stats.loadingPromises}</span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(stats.cacheSize / stats.maxCacheSize) * 100}%` }}
            ></div>
          </div>

          <div className="flex gap-2 mt-3">
            <button
              onClick={handleClearCache}
              className="flex-1 bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 transition-colors flex items-center justify-center"
            >
              <Trash2 size={12} className="mr-1" />
              Clear
            </button>
            
            <button
              onClick={() => setStats(getCacheStats())}
              className="flex-1 bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600 transition-colors flex items-center justify-center"
            >
              <RefreshCw size={12} className="mr-1" />
              Refresh
            </button>
          </div>

          <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
            <div className="flex items-center text-gray-600 mb-1">
              <Info size={12} className="mr-1" />
              Cache Status
            </div>
            <div className="text-gray-500">
              Images are cached to prevent reloading when navigating between pages.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCacheManager;
